/**
 * AffiliatesAdmin — только для ролей с perm affiliate:manage.
 * Позволяет:
 *   - видеть всех аффилиатов и их балансы
 *   - создавать / редактировать рефкоды (commission_pct)
 *   - обрабатывать заявки на выплату (mark_processing / mark_paid / mark_failed / retry)
 */
import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { Icon } from '../components/icons';
import { PageHeader, SectionCard, Spinner } from '../components/ui';
import { fmtMoney } from '../lib/format';

interface AffiliateListItem {
  id: string;
  username: string;
  display_name: string;
  active: boolean;
  created_at: string;
  balance: string;
  hold_balance: string;
  total_earned: string;
  total_withdrawn: string;
  player_count: string;
  link_count: string;
}

interface AdminLinkRow extends AffiliateLinkRow {
  display_name: string;
  username: string;
}

interface AdminPayoutRow {
  id: string;
  affiliate_user_id: string;
  amount: string;
  crypto_network: string;
  crypto_currency: string;
  crypto_address: string;
  heleket_payout_id: string | null;
  status: 'pending' | 'processing' | 'paid' | 'failed' | 'cancelled';
  failure_reason: string | null;
  created_at: string;
  processed_at: string | null;
  username: string;
  display_name: string;
}

interface AffiliateLinkRow {
  id: string;
  affiliate_user_id: string;
  code: string;
  commission_pct: string;
  clicks: number;
  registrations: number;
  active: boolean;
  notes: string | null;
  created_at: string;
}

type Tab = 'affiliates' | 'links' | 'payouts';

export function AffiliatesAdminPage() {
  const [tab, setTab] = useState<Tab>('affiliates');

  return (
    <div>
      <PageHeader title="Аффилиаты · Администрирование" subtitle="Управление партнёрами, рефкодами и выплатами" />

      <div className="flex flex-wrap gap-1 mb-4 bg-black/30 rounded-xl p-1 border border-white/10">
        <TabButton active={tab === 'affiliates'} onClick={() => setTab('affiliates')}>Аффилиаты</TabButton>
        <TabButton active={tab === 'links'} onClick={() => setTab('links')}>Рефссылки</TabButton>
        <TabButton active={tab === 'payouts'} onClick={() => setTab('payouts')}>Заявки на выплату</TabButton>
      </div>

      {tab === 'affiliates' && <AffiliatesListTab />}
      {tab === 'links' && <LinksAdminTab />}
      {tab === 'payouts' && <PayoutsAdminTab />}
    </div>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={
        'flex-1 min-w-[120px] px-3 py-2 text-sm rounded-lg transition ' +
        (active ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'text-stone-300 hover:bg-white/5')
      }
    >
      {children}
    </button>
  );
}

function AffiliatesListTab() {
  const [items, setItems] = useState<AffiliateListItem[]>([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get<{ items: AffiliateListItem[] }>('/api/crm/affiliates-admin/players')
      .then((r) => setItems(r.items))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="flex items-center gap-2 text-stone-400 text-sm"><Spinner /> Загрузка…</div>;

  return (
    <SectionCard title="Все аффилиаты" subtitle={`Всего: ${items.length}`}>
      {items.length === 0 ? (
        <div className="text-sm text-stone-500 text-center py-4">Пока нет зарегистрированных аффилиатов. Создайте CRM-пользователя с ролью «affiliate» в разделе «Команда».</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-stone-400">
              <tr>
                <th className="px-3 py-2">Логин / Имя</th>
                <th className="px-3 py-2">Создан</th>
                <th className="px-3 py-2">Активен</th>
                <th className="px-3 py-2">Баланс</th>
                <th className="px-3 py-2">В холде</th>
                <th className="px-3 py-2">Заработано</th>
                <th className="px-3 py-2">Выплачено</th>
                <th className="px-3 py-2">Игроков</th>
                <th className="px-3 py-2">Ссылок</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} className="border-t border-white/5">
                  <td className="px-3 py-2">
                    <div className="text-stone-100">{a.display_name}</div>
                    <div className="text-xs text-stone-500 font-mono">{a.username}</div>
                  </td>
                  <td className="px-3 py-2 text-xs text-stone-400">{fmtTs(a.created_at)}</td>
                  <td className="px-3 py-2 text-xs">
                    {a.active
                      ? <span className="text-emerald-300">да</span>
                      : <span className="text-rose-300">нет</span>}
                  </td>
                  <td className="px-3 py-2 font-mono">{fmtMoney(num(a.balance))}</td>
                  <td className="px-3 py-2 font-mono text-amber-300">{fmtMoney(num(a.hold_balance))}</td>
                  <td className="px-3 py-2 font-mono">{fmtMoney(num(a.total_earned))}</td>
                  <td className="px-3 py-2 font-mono text-emerald-300">{fmtMoney(num(a.total_withdrawn))}</td>
                  <td className="px-3 py-2 font-mono">{a.player_count}</td>
                  <td className="px-3 py-2 font-mono">{a.link_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}

function LinksAdminTab() {
  const [items, setItems] = useState<AdminLinkRow[]>([]);
  const [affiliates, setAffiliates] = useState<AffiliateListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    affiliateUserId: '',
    commissionPct: '20',
    notes: ''
  });
  const [autoCode, setAutoCode] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get<{ items: AdminLinkRow[] }>('/api/crm/affiliates-admin/links'),
      api.get<{ items: AffiliateListItem[] }>('/api/crm/affiliates-admin/players')
    ])
      .then(([linksR, affR]) => {
        setItems(linksR.items);
        setAffiliates(affR.items);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // При смене аффилиата в форме — генерируем удобный код автоматически.
  // Берём username (если не занят в affiliate_links), иначе username + 4 символа.
  useEffect(() => {
    if (!form.affiliateUserId) {
      setAutoCode('');
      return;
    }
    const aff = affiliates.find((a) => a.id === form.affiliateUserId);
    if (!aff) {
      setAutoCode('');
      return;
    }
    const base = aff.username.toLowerCase().replace(/[^a-z0-9_-]/g, '');
    if (base.length < 2) {
      setAutoCode(`aff_${aff.id.slice(0, 6)}`);
      return;
    }
    // Если username уже использован — добавим суффикс
    const used = new Set(items.map((l) => l.code));
    if (!used.has(base)) {
      setAutoCode(base);
    } else {
      const suffix = Math.random().toString(36).slice(2, 6);
      setAutoCode(`${base}_${suffix}`);
    }
  }, [form.affiliateUserId, affiliates, items]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.affiliateUserId) {
      setError('Выберите аффилиата из списка.');
      return;
    }
    if (!autoCode || autoCode.length < 2) {
      setError('Код не сгенерирован.');
      return;
    }
    setCreating(true);
    try {
      await api.post('/api/crm/affiliates-admin/links', {
        affiliateUserId: form.affiliateUserId,
        code: autoCode.trim(),
        commissionPct: Number(form.commissionPct),
        notes: form.notes.trim() || undefined
      });
      setForm({ affiliateUserId: '', commissionPct: '20', notes: '' });
      setAutoCode('');
      await load();
    } catch (e: any) {
      setError(e?.error === 'CODE_TAKEN' ? 'Этот код уже занят. Поменяйте суффикс.' : (e?.message ?? 'Не удалось создать рефкод.'));
    } finally {
      setCreating(false);
    }
  };

  const update = async (id: string, patch: { commissionPct?: number; active?: boolean }) => {
    try {
      await api.patch(`/api/crm/affiliates-admin/links/${id}`, patch);
      await load();
    } catch (e: any) {
      window.alert('Ошибка: ' + (e?.message ?? 'unknown'));
    }
  };

  // Аффилиаты без активной ссылки — наверху списка (чтобы не плодить дубли).
  const sortedAffiliates = useMemo(() => {
    const codeByAffiliate = new Map<string, number>();
    for (const l of items) {
      codeByAffiliate.set(l.affiliate_user_id, (codeByAffiliate.get(l.affiliate_user_id) ?? 0) + 1);
    }
    return [...affiliates].sort((a, b) => {
      const ac = codeByAffiliate.get(a.id) ?? 0;
      const bc = codeByAffiliate.get(b.id) ?? 0;
      if (ac !== bc) return ac - bc;
      return a.display_name.localeCompare(b.display_name);
    });
  }, [affiliates, items]);

  return (
    <div className="space-y-4">
      <SectionCard title="Создать рефссылку">
        <form className="space-y-3" onSubmit={submit}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Аффилиат (имя / логин)">
              <select
                className="input-modern"
                value={form.affiliateUserId}
                onChange={(e) => setForm({ ...form, affiliateUserId: e.target.value })}
                required
              >
                <option value="">— выберите из списка —</option>
                {sortedAffiliates.map((a) => {
                  const cnt = items.filter((l) => l.affiliate_user_id === a.id).length;
                  return (
                    <option key={a.id} value={a.id}>
                      {a.display_name} (@{a.username}){cnt > 0 ? ` · уже ${cnt} ссыл.` : ''}
                    </option>
                  );
                })}
              </select>
            </Field>
            <Field label="Код (генерируется автоматически)">
              <div className="flex gap-2">
                <input
                  className="input-modern flex-1 font-mono"
                  value={autoCode}
                  onChange={(e) => setAutoCode(e.target.value.replace(/[^A-Za-z0-9_-]/g, ''))}
                  maxLength={40}
                  placeholder="выберите аффилиата"
                />
                <button
                  type="button"
                  className="btn-gold !px-3 !py-2 text-xs whitespace-nowrap"
                  disabled={!form.affiliateUserId}
                  onClick={() => {
                    const aff = affiliates.find((a) => a.id === form.affiliateUserId);
                    if (!aff) return;
                    setAutoCode(`${aff.username.toLowerCase().replace(/[^a-z0-9_-]/g, '')}_${Math.random().toString(36).slice(2, 6)}`);
                  }}
                  title="Сгенерировать заново"
                >
                  ⟳ Заново
                </button>
              </div>
              {autoCode ? (
                <div className="text-[11px] text-stone-500 mt-1">
                  URL: <span className="font-mono text-amber-300">https://buraclub.online/r/{autoCode}</span>
                </div>
              ) : null}
            </Field>
            <Field label="% комиссии (0–100)">
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                className="input-modern"
                value={form.commissionPct}
                onChange={(e) => setForm({ ...form, commissionPct: e.target.value })}
                required
              />
            </Field>
          </div>
          <Field label="Заметка (необязательно)">
            <input
              className="input-modern"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              maxLength={500}
              placeholder="например: «Трафик из Telegram-канала @xyz»"
            />
          </Field>
          <div className="flex items-center gap-2">
            <button className="btn-gold px-4 py-2 text-sm" disabled={creating || !form.affiliateUserId || !autoCode}>
              {creating ? 'Создание…' : 'Создать рефссылку'}
            </button>
            {error ? <span className="text-sm text-rose-300">{error}</span> : null}
            <span className="text-xs text-stone-500 ml-auto">
              Всего аффилиатов: {affiliates.length} · Всего ссылок: {items.length}
            </span>
          </div>
        </form>
      </SectionCard>

      <SectionCard title={`Все рефссылки (${items.length})`}>
        {loading ? (
          <div className="flex items-center gap-2 text-stone-400 text-sm"><Spinner /> Загрузка…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-stone-500 text-center py-4">Ссылок пока нет.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-stone-400">
                <tr>
                  <th className="px-3 py-2">Код</th>
                  <th className="px-3 py-2">Аффилиат</th>
                  <th className="px-3 py-2">%</th>
                  <th className="px-3 py-2">Кликов</th>
                  <th className="px-3 py-2">Регистраций</th>
                  <th className="px-3 py-2">Активна</th>
                  <th className="px-3 py-2">Создана</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((l) => (
                  <tr key={l.id} className="border-t border-white/5">
                    <td className="px-3 py-2 font-mono text-amber-300">{l.code}</td>
                    <td className="px-3 py-2">
                      <div className="text-stone-100">{l.display_name}</div>
                      <div className="text-xs text-stone-500 font-mono">{l.username}</div>
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        defaultValue={l.commission_pct}
                        className="input-modern w-24"
                        onBlur={(e) => {
                          const v = Number(e.target.value);
                          if (Number.isFinite(v) && v !== Number(l.commission_pct)) {
                            void update(l.id, { commissionPct: v });
                          }
                        }}
                      />
                    </td>
                    <td className="px-3 py-2 font-mono">{l.clicks}</td>
                    <td className="px-3 py-2 font-mono">{l.registrations}</td>
                    <td className="px-3 py-2">
                      <label className="flex items-center gap-1 text-xs">
                        <input type="checkbox" defaultChecked={l.active} onChange={(e) => void update(l.id, { active: e.target.checked })} />
                        {l.active ? 'да' : 'нет'}
                      </label>
                    </td>
                    <td className="px-3 py-2 text-xs text-stone-400">{fmtTs(l.created_at)}</td>
                    <td className="px-3 py-2 text-xs text-stone-500 max-w-[200px] truncate" title={l.notes ?? ''}>
                      {l.notes ?? ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
}

function PayoutsAdminTab() {
  const [items, setItems] = useState<AdminPayoutRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<string>('');

  const load = () => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (status) qs.set('status', status);
    qs.set('limit', '200');
    api.get<{ items: AdminPayoutRow[]; total: number }>(`/api/crm/affiliates-admin/payouts?${qs.toString()}`)
      .then((r) => { setItems(r.items); setTotal(r.total); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const process = async (id: string, action: 'mark_paid' | 'mark_failed' | 'mark_processing' | 'retry', extra?: { heleketPayoutId?: string; failureReason?: string }) => {
    try {
      await api.post(`/api/crm/affiliates-admin/payouts/${id}/process`, { action, ...extra });
      await load();
    } catch (e: any) {
      window.alert('Ошибка: ' + (e?.message ?? JSON.stringify(e)));
    }
  };

  const askMarkPaid = (p: AdminPayoutRow) => {
    const heleket = window.prompt('Heleket payout ID (опционально):', '');
    if (heleket === null) return;
    void process(p.id, 'mark_paid', { heleketPayoutId: heleket.trim() || undefined });
  };

  const askMarkFailed = (p: AdminPayoutRow) => {
    const reason = window.prompt('Причина ошибки:', '');
    if (reason === null) return;
    void process(p.id, 'mark_failed', { failureReason: reason.trim() || undefined });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-xs text-stone-400">Фильтр по статусу:</span>
        <select className="input-modern w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Все</option>
          <option value="pending">Ожидают</option>
          <option value="processing">В обработке</option>
          <option value="paid">Выплачено</option>
          <option value="failed">Ошибка</option>
          <option value="cancelled">Отменены</option>
        </select>
        <span className="ml-auto text-xs text-stone-500">Всего: {total}</span>
      </div>

      <SectionCard title="Заявки на выплату">
        {loading ? (
          <div className="flex items-center gap-2 text-stone-400 text-sm"><Spinner /> Загрузка…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-stone-500 text-center py-4">Заявок нет.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-stone-400">
                <tr>
                  <th className="px-3 py-2">Дата</th>
                  <th className="px-3 py-2">Аффилиат</th>
                  <th className="px-3 py-2">Сумма</th>
                  <th className="px-3 py-2">Сеть / Валюта</th>
                  <th className="px-3 py-2">Кошелёк</th>
                  <th className="px-3 py-2">Статус</th>
                  <th className="px-3 py-2">Heleket ID</th>
                  <th className="px-3 py-2">Действие</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} className="border-t border-white/5 align-top">
                    <td className="px-3 py-2 text-xs text-stone-400 whitespace-nowrap">{fmtTs(p.created_at)}</td>
                    <td className="px-3 py-2">
                      <div className="text-stone-100">{p.display_name}</div>
                      <div className="text-xs text-stone-500 font-mono">{p.username}</div>
                    </td>
                    <td className="px-3 py-2 font-mono">{fmtMoney(num(p.amount))}</td>
                    <td className="px-3 py-2 text-xs">
                      {p.crypto_network}<br />
                      <span className="text-stone-500">{p.crypto_currency}</span>
                    </td>
                    <td className="px-3 py-2 text-xs font-mono max-w-[180px] break-all" title={p.crypto_address}>
                      {p.crypto_address}
                    </td>
                    <td className="px-3 py-2">
                      <PayoutStatusBadge status={p.status} />
                      {p.failure_reason ? (
                        <div className="text-[10px] text-rose-300 mt-1">{p.failure_reason}</div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-xs font-mono text-stone-500">
                      {p.heleket_payout_id ?? '—'}
                    </td>
                    <td className="px-3 py-2">
                      {p.status === 'pending' && (
                        <div className="flex flex-col gap-1">
                          <button className="text-xs px-2 py-1 rounded border border-sky-400/30 text-sky-300 hover:bg-sky-500/10" onClick={() => process(p.id, 'mark_processing')}>
                            В обработку
                          </button>
                          <button className="text-xs px-2 py-1 rounded border border-emerald-400/30 text-emerald-300 hover:bg-emerald-500/10" onClick={() => askMarkPaid(p)}>
                            Выплачено
                          </button>
                          <button className="text-xs px-2 py-1 rounded border border-rose-400/30 text-rose-300 hover:bg-rose-500/10" onClick={() => askMarkFailed(p)}>
                            Ошибка
                          </button>
                        </div>
                      )}
                      {p.status === 'processing' && (
                        <div className="flex flex-col gap-1">
                          <button className="text-xs px-2 py-1 rounded border border-emerald-400/30 text-emerald-300 hover:bg-emerald-500/10" onClick={() => askMarkPaid(p)}>
                            Подтвердить оплату
                          </button>
                          <button className="text-xs px-2 py-1 rounded border border-rose-400/30 text-rose-300 hover:bg-rose-500/10" onClick={() => askMarkFailed(p)}>
                            Ошибка
                          </button>
                        </div>
                      )}
                      {(p.status === 'failed' || p.status === 'cancelled') && (
                        <button className="text-xs px-2 py-1 rounded border border-amber-400/30 text-amber-300 hover:bg-amber-500/10" onClick={() => process(p.id, 'retry')}>
                          Возобновить
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
}

function PayoutStatusBadge({ status }: { status: AdminPayoutRow['status'] }) {
  const colors: Record<AdminPayoutRow['status'], string> = {
    pending: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    processing: 'bg-sky-500/20 text-sky-300 border-sky-500/30',
    paid: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
    failed: 'bg-rose-500/20 text-rose-300 border-rose-500/30',
    cancelled: 'bg-stone-500/20 text-stone-300 border-stone-500/30'
  };
  const labels: Record<AdminPayoutRow['status'], string> = {
    pending: 'Ожидает',
    processing: 'В обработке',
    paid: 'Выплачено',
    failed: 'Ошибка',
    cancelled: 'Отменена'
  };
  return (
    <span className={'inline-block px-2 py-0.5 text-xs rounded border ' + colors[status]}>
      {labels[status]}
    </span>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="block text-xs text-stone-400 mb-1">{label}</span>
      {children}
    </label>
  );
}

function num(v: string | number | undefined | null): number {
  if (v === undefined || v === null) return 0;
  return Number(v);
}

function fmtTs(ts: string | number | null | undefined): string {
  if (!ts) return '—';
  const n = typeof ts === 'string' ? Number(ts) : ts;
  if (!Number.isFinite(n)) return '—';
  return new Date(n).toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit'
  });
}
